import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageTemplate from './PageTemplate';
import { UnifiedHeader } from './UnifiedHeader';
import { useAuth } from '../contexts/AuthContext';
import { useSpace } from '../contexts/SpaceContext';
import { useSystemStatus } from '../contexts/SystemStatusContext';
import { useFontSize } from '../hooks/useFontSize';
import AuthModal from './AuthModal';
import { MaintenanceBanner } from './MaintenanceBanner';
import { HelpSearchBar, HelpSearchBarRef } from './HelpSearchBar';
import { Platform } from '../utils/platform';
import API_BASE_URL from '../config';
import '../styles/components/help.css';
import '../styles/components/publicHelp.css';
import '../styles/components/publicHelpFloatingCTA.css';

import SafeMarkdown from './SafeMarkdown';

interface HelpPageProps {
  isActive: boolean;
  isPublic?: boolean; // If true, renders as standalone public page with CTA
  onTabChange?: (tab: 'home' | 'inventory' | 'groups' | 'profile' | 'market') => void;
  onFontSizeToggle?: () => void;
  fontSize?: string;
  onOpenAppContext?: () => void;
}

type HelpSection = string;

const HelpPage: React.FC<HelpPageProps> = ({ 
  isActive, 
  isPublic = false, 
  onTabChange, 
  onFontSizeToggle, 
  fontSize: fontSizeProp, 
  onOpenAppContext
}) => {
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSpace } = useSpace();
  const { systemStatus, isMaintenanceMode } = useSystemStatus();
  const { fontSize: fontSizeHook, setFontSize } = useFontSize();
  
  // Use prop fontSize if provided (logged-in mode), otherwise use hook (public mode)
  const fontSize = fontSizeProp || fontSizeHook;
  
  // Redirect authenticated users from public Help page to main app
  useEffect(() => {
    if (isPublic && user) {
      navigate('/', { replace: true });
    }
  }, [isPublic, user, navigate]);
  
  // Add capacitor-app class for public pages (logged-in pages get it from AppContent)
  useEffect(() => {
    if (isPublic && Platform.isNative) {
      document.documentElement.classList.add('capacitor-app');
      document.body.classList.add('capacitor-app');
    }
  }, [isPublic]);
  
  // Font size cycling function for public mode
  const cycleFontSize = () => {
    const sizes: ('small' | 'medium' | 'large' | 'x-large')[] = ['small', 'medium', 'large', 'x-large'];
    const currentIndex = sizes.indexOf(fontSizeHook);
    const nextIndex = (currentIndex + 1) % sizes.length;
    setFontSize(sizes[nextIndex]);
  };
  
  // Get font size number for display
  const getFontSizeNumber = () => {
    const sizeMap = { 'small': '1', 'medium': '2', 'large': '3', 'x-large': '4' };
    return sizeMap[fontSizeHook];
  };
  const [activeSection, setActiveSection] = useState<HelpSection>((section as HelpSection) || 'about');
  
  // Keep ref in sync with state
  useEffect(() => {
    activeSectionRef.current = activeSection;
  }, [activeSection]);
  const [markdownContent, setMarkdownContent] = useState<Record<string, string>>({});
  const [sections, setSections] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const searchBarRef = useRef<HelpSearchBarRef>(null);
  const scrollHandlerRef = useRef<((e: Event) => void) | null>(null);
  const isProgrammaticScrollRef = useRef(false);
  const sectionRefsMap = useRef<Record<string, React.RefObject<HTMLElement | null>>>({});
  
  // Touch gesture tracking
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const lastTapTimeRef = useRef<number>(0);
  const activeSectionRef = useRef<HelpSection>(activeSection);
  
  // Helper to get or create a ref for a section
  const getSectionRef = (section: string): React.RefObject<HTMLElement | null> => {
    if (!sectionRefsMap.current[section]) {
      sectionRefsMap.current[section] = React.createRef<HTMLElement>();
    }
    return sectionRefsMap.current[section];
  };
  
  // Fetch available sections from API
  useEffect(() => {
    const fetchSections = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/help/sections`);
        if (response.ok) {
          const data = await response.json();
          setSections(data.sections || []);
        } else {
          // Fallback to default sections
          setSections(['about', 'why', 'quick-start', 'concepts', 'inventory', 'collections', 'search', 'bindles', 'add-items', 'recipes', 'voice', 'sharing', 'trash', 'use-cases', 'examples', 'tips', 'contact', 'privacy', 'terms']);
        }
      } catch (error) {
        console.error('Failed to fetch help sections:', error);
        // Fallback to default sections
        setSections(['about', 'why', 'quick-start', 'concepts', 'inventory', 'collections', 'search', 'bindles', 'add-items', 'recipes', 'voice', 'sharing', 'trash', 'use-cases', 'examples', 'tips', 'contact', 'privacy', 'terms']);
      }
    };
    
    fetchSections();
  }, []);
  
  // Update active section when URL changes (public mode)
  const hasScrolledOnLoad = useRef(false);
  useEffect(() => {
    if (isPublic && section && sections.includes(section)) {
      if (activeSectionRef.current !== section) {
        // Section changed via navigation — scroll to it
        setActiveSection(section);
        if (!loading) {
          setTimeout(() => {
            scrollToSection(section);
          }, 100);
        }
      } else if (!loading && !hasScrolledOnLoad.current && section !== sections[0]) {
        // Initial load with a deep-link section (e.g. /help/privacy) — scroll once
        hasScrolledOnLoad.current = true;
        setTimeout(() => {
          scrollToSection(section);
        }, 200);
      }
    }
  }, [section, loading, isPublic, sections]);
  
  // Load markdown files after sections are loaded
  useEffect(() => {
    if (sections.length === 0) return;
    
    const loadMarkdown = async () => {
      // Try API endpoint first (for latest content with CORS headers)
      // Fall back to bundled files if API is unavailable (maintenance mode or backend down)
      
      const fetchHelpContent = async (section: string): Promise<string> => {
        // Try API first with cache-busting
        try {
          // Add timestamp to bust cache and ensure fresh content
          const cacheBuster = Date.now();
          const response = await fetch(`${API_BASE_URL}/api/help/${section}?v=${cacheBuster}`, {
            cache: 'no-cache',
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache'
            }
          });
          if (response.ok) {
            return await response.text();
          }
        } catch (error) {
          console.log(`API unavailable for ${section}, falling back to bundled file`);
        }
        
        // Fall back to bundled file (also with cache-busting for iOS)
        try {
          const cacheBuster = Date.now();
          const response = await fetch(`/help/${section}.md?v=${cacheBuster}`, {
            cache: 'no-cache'
          });
          if (response.ok) {
            return await response.text();
          }
        } catch (error) {
          console.error(`Failed to load bundled ${section}.md:`, error);
        }
        
        return `# ${section}\n\nContent not available.`;
      };
      
      // 1. Load the primary 'about' section first so the user sees
      // content as quickly as possible.
      try {
        const aboutText = await fetchHelpContent('about');

        setMarkdownContent(prev => ({
          ...prev,
          about: aboutText,
        }));
        setLoading(false);

        // 2. Load remaining sections in the background and merge
        const remainingSections = sections.filter((s) => s !== 'about');
        const entries = await Promise.all(
          remainingSections.map(async (section) => {
            const text = await fetchHelpContent(section);
            return [section, text] as const;
          })
        );

        setMarkdownContent(prev => {
          const updated: Record<string, string> = { ...prev };
          for (const [section, text] of entries) {
            updated[section] = text;
          }
          return updated;
        });
      } catch (e) {
        // If something unexpected happens, at least ensure loading is false
        setLoading(false);
      }
    };
    
    loadMarkdown();
  }, [sections]);
  
  // Helper to format section name for display
  const formatSectionName = (section: string): string => {
    // Special cases with emojis
    const specialCases: Record<string, string> = {
      'recipes': '🍳 Recipes',
      'sharing': '👥 Sharing',
      'moving-company': '🚚 Moving Companies',
      'marketplace': '🏪 Marketplace',
      'auctions': '🔨 Auctions',
      'voice': '🎤 Voice',
      'use-cases': '💡 Use Cases',
    };
    
    if (specialCases[section]) {
      return specialCases[section];
    }
    
    // Convert kebab-case to Title Case
    return section
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  // Navigation helpers
  const navigateToNextSection = () => {
    const currentIndex = sections.indexOf(activeSection);
    if (currentIndex < sections.length - 1) {
      scrollToSection(sections[currentIndex + 1]);
    }
  };
  
  const navigateToPrevSection = () => {
    const currentIndex = sections.indexOf(activeSection);
    if (currentIndex > 0) {
      scrollToSection(sections[currentIndex - 1]);
    }
  };
  
  const navigateToFirstSection = () => {
    if (sections.length > 0) {
      scrollToSection(sections[0]);
    }
  };
  
  const navigateToLastSection = () => {
    if (sections.length > 0) {
      scrollToSection(sections[sections.length - 1]);
    }
  };
  
  const scrollToSection = (section: string) => {
    const element = getSectionRef(section).current;
    if (element) {
      // Update active section immediately
      setActiveSection(section);
      
      // Update URL in public mode
      if (isPublic && navigate) {
        navigate(`/help/${section}`, { replace: true });
      }
      
      // Set flag to ignore scroll events during programmatic scrolling
      isProgrammaticScrollRef.current = true;
      
      // Get the scroll container - detect based on environment
      const isCapacitor = document.body.classList.contains('capacitor-app');
      const scrollContainer = isCapacitor 
        ? document.querySelector('.tab-scroll-container.home-tab') as HTMLElement
        : document.querySelector('.help-page .page-template-body') as HTMLElement;
      
      if (!scrollContainer) {
        console.log('[scrollToSection] Scroll container not found');
        isProgrammaticScrollRef.current = false;
        return;
      }
      
      // Get current positions
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      
      // Calculate scroll to position section at top of container
      // Unified logic for both platforms: scroll to element position minus small offset
      const currentScroll = scrollContainer.scrollTop;
      const elementRelativeTop = elementRect.top - containerRect.top;
      const SCROLL_OFFSET = 6; // Small offset to avoid showing bottom of previous section
      const targetScroll = currentScroll + elementRelativeTop - SCROLL_OFFSET;
      
      // Scroll the container - use auto for instant swipe navigation responsiveness
      scrollContainer.scrollTo({ top: targetScroll, behavior: 'auto' });
      
      // Re-enable scroll listener after scroll completes
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 1000);
      
      // Scroll the nav button for the target section into view
      setTimeout(() => {
        if (navRef.current) {
          const targetButton = navRef.current.querySelector(`[data-section="${section}"]`) as HTMLElement;
          if (targetButton) {
            targetButton.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center'
            });
          }
        }
      }, 100);
    }
  };
  
  // Touch gesture handlers
  useEffect(() => {
    if (loading) return;
    
    const isCapacitor = document.body.classList.contains('capacitor-app');
    const scrollContainer = isCapacitor 
      ? document.querySelector('.tab-scroll-container.home-tab') as HTMLElement
      : document.querySelector('.help-page .page-template-body') as HTMLElement;
    
    if (!scrollContainer) return;
    
    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now()
      };
    };
    
    const handleTouchEnd = (e: TouchEvent) => {
      if (!touchStartRef.current) return;
      
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;
      const deltaTime = Date.now() - touchStartRef.current.time;
      
      // Check for double tap (minimal movement)
      const timeSinceLastTap = Date.now() - lastTapTimeRef.current;
      if (timeSinceLastTap < 300 && Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10) {
        // Double tap detected - scroll to beginning of current section
        scrollToSection(activeSectionRef.current);
        lastTapTimeRef.current = 0; // Reset
        touchStartRef.current = null;
        return;
      }
      lastTapTimeRef.current = Date.now();
      
      // Ignore if vertical scroll is dominant (user is scrolling, not swiping)
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        touchStartRef.current = null;
        return;
      }
      
      // Require minimum horizontal distance
      const minSwipeDistance = 50;
      if (Math.abs(deltaX) < minSwipeDistance) {
        touchStartRef.current = null;
        return;
      }
      
      // Check if search is active
      const hasSearch = searchBarRef.current?.hasActiveSearch();
      
      if (hasSearch) {
        // Navigate search matches
        if (deltaX > 0) {
          // Swipe right - go to previous match
          searchBarRef.current?.navigateToPrev();
        } else {
          // Swipe left - go to next match
          searchBarRef.current?.navigateToNext();
        }
      } else {
        // Navigate sections
        if (deltaX > 0) {
          // Swipe right - go to previous section
          const currentIndex = sections.indexOf(activeSectionRef.current);
          if (currentIndex > 0) {
            scrollToSection(sections[currentIndex - 1]);
          }
        } else {
          // Swipe left - go to next section
          const currentIndex = sections.indexOf(activeSectionRef.current);
          if (currentIndex < sections.length - 1) {
            scrollToSection(sections[currentIndex + 1]);
          }
        }
      }
      
      touchStartRef.current = null;
    };
    
    scrollContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
    scrollContainer.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    return () => {
      scrollContainer.removeEventListener('touchstart', handleTouchStart);
      scrollContainer.removeEventListener('touchend', handleTouchEnd);
    };
  }, [loading, sections]);
  
  // Keep nav active state in sync with manual scrolling
  useEffect(() => {
    // Wait for markdown content to load
    if (loading) {
      return;
    }
    
    // Detect which element is scrollable based on environment
    // Webapp: .page-template-body (position: absolute with overflow-y: auto)
    // Capacitor: .tab-scroll-container.home-tab (position: absolute with overflow: auto)
    const isCapacitor = document.body.classList.contains('capacitor-app');
    const scrollContainer = isCapacitor 
      ? document.querySelector('.tab-scroll-container.home-tab') as HTMLElement
      : document.querySelector('.help-page .page-template-body') as HTMLElement;
      
    if (!scrollContainer) {
      return;
    }
    
    // Remove old listener if it exists
    if (scrollHandlerRef.current) {
      scrollContainer.removeEventListener('scroll', scrollHandlerRef.current);
    }
    
    const handleScroll = () => {
      if (isProgrammaticScrollRef.current) {
        return;
      }

      let closestSection: HelpSection | null = null;
      
      // Get container's position
      const containerRect = scrollContainer.getBoundingClientRect();
      const scrollTop = scrollContainer.scrollTop;

      // Find which section is currently at the top of the container
      // Unified logic for both platforms
      for (const section of sections) {
        const el = getSectionRef(section).current;
        if (!el) continue;
        
        // Get element's position relative to container
        const rect = el.getBoundingClientRect();
        const sectionTop = rect.top;
        const sectionBottom = rect.bottom;
        
        // Section is active if it's at the top of the container (with threshold for half-visible)
        // containerRect.top is at 181px on both platforms now
        const threshold = containerRect.top + 100; // Allow section to be slightly scrolled past
        const isInView = sectionTop <= threshold && sectionBottom > containerRect.top;
        
        if (isInView) {
          closestSection = section;
          // Don't break - keep checking to find the last section that's in view
        }
      }

      if (!closestSection) return;

      const targetSection = closestSection as HelpSection;

      setActiveSection(prev => {
        if (prev === targetSection) {
          return prev;
        }
        return targetSection;
      });

      // Ensure the active nav button stays visible in the horizontal menu
      if (navRef.current) {
        const targetButton = navRef.current.querySelector(`[data-section="${targetSection}"]`) as HTMLElement | null;
        if (targetButton) {
          targetButton.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        }
      }
    };

    // Store handler in ref
    scrollHandlerRef.current = handleScroll;
    
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    // Initial sync after mount/restore
    handleScroll();

    return () => {
      if (scrollHandlerRef.current) {
        scrollContainer.removeEventListener('scroll', scrollHandlerRef.current);
      }
    };
  }, [loading, sections]);
  
  const helpNav = (
    <div className="help-nav-container">
      <nav className="help-nav transparent-menu-bar" ref={navRef}>
        {/* Search bar - sticky at left, seamlessly integrated */}
        <HelpSearchBar
          ref={searchBarRef}
          sections={sections}
          sectionRefs={sectionRefsMap.current}
          onNavigateToMatch={(sectionId) => scrollToSection(sectionId)}
          inMenuBar={true}
          activeSection={activeSection}
          onSectionClick={(section) => scrollToSection(section)}
        />
        
        {/* Section buttons - dynamically generated from API */}
        {sections.map((section, index) => (
          <button
            key={section}
            className={`help-nav-btn ${index === 0 ? 'help-nav-btn-first' : ''} ${activeSection === section ? 'active' : ''}`}
            onClick={() => scrollToSection(section)}
            data-section={section}
          >
            {formatSectionName(section)}
          </button>
        ))}
      </nav>
    </div>
  );

  // Render public standalone page
  if (isPublic) {
    return (
      <div className="App mobile-app">
        {/* Show maintenance banner if system is down */}
        {isMaintenanceMode && (
          <MaintenanceBanner
            message={systemStatus.message}
            estimatedCompletion={systemStatus.estimated_completion}
          />
        )}
        
        <div className="main-content" style={{ position: 'relative', overflow: 'hidden' }}>
          <div className="tab-scroll-container home-tab" style={{ display: 'block' }}>
            <UnifiedHeader
              title="Help"
              subtitle="Help & Documentation"
              showFontSizeToggle={true}
              onFontSizeToggle={cycleFontSize}
              fontSize={getFontSizeNumber().toString()}
              menuBar={helpNav}
            />
            <PageTemplate
              showPageHeader={false}
              showInputArea={false}
              hasGrayBackground={false}
              enableSmartScroll={false}
              className="help-page public-help-page"
            >
              {loading ? (
                <div className="help-section">
                  <p>Loading help content...</p>
                </div>
              ) : (
                <>
                  {sections.map((section) => (
                    <section
                      key={section}
                      ref={getSectionRef(section)}
                      className="help-section markdown-content"
                      id={section}
                    >
                      <SafeMarkdown useGfm>{markdownContent[section] || ''}</SafeMarkdown>
                    </section>
                  ))}
                </>
              )}
              
              {/* Floating CTA button - only show for non-authenticated users and when system is up */}
              {!user && !isMaintenanceMode && (
                <button 
                  className="public-help-floating-cta"
                  onClick={() => setShowAuthModal(true)}
                >
                  Sign in / Sign up
                </button>
              )}
            </PageTemplate>
          </div>
          
          {/* Auth Modal for public page - disabled during maintenance */}
          {!isMaintenanceMode && (
            <AuthModal
              isOpen={showAuthModal}
              onClose={() => setShowAuthModal(false)}
              initialMode="login"
              onLoginSuccess={() => {
                setShowAuthModal(false);
                navigate('/');
              }}
            />
          )}
        </div>
      </div>
    );
  }

  // Render logged-in app page
  return (
    <>
      {isActive && (
        <UnifiedHeader 
          title="Help"
          subtitle="Photos → Searchable Items"
          menuBar={helpNav}
          onFontSizeToggle={onFontSizeToggle}
          fontSize={fontSize}
          onOpenAppContext={onOpenAppContext}
          activeSpaceName={activeSpace?.name}
        />
      )}
      <PageTemplate
        showPageHeader={false}
        showInputArea={false}
        hasGrayBackground={false}
        enableSmartScroll={false}
        className="help-page"
      >
        {loading ? (
          <div className="help-section">
            <p>Loading help content...</p>
          </div>
        ) : (
          <>
            {sections.map((section) => (
              <section 
                key={section} 
                ref={getSectionRef(section)} 
                className="help-section markdown-content" 
                id={section}
              >
                <SafeMarkdown useGfm>{markdownContent[section] || ''}</SafeMarkdown>
              </section>
            ))}
          </>
        )}
      </PageTemplate>
    </>
  );
};

export default HelpPage;
